import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthService, StateWithProcess } from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, map, share, switchMap } from 'rxjs/operators';
import { UserIdService } from '../../auth';
import { SearchAction } from '../../features/store/actions/search.actions';
import { OrderApproval, OrderApprovalSearchParams, Pagination, Sort } from '../../model';
import { withdrawOn } from '../../util';
import { OrderApprovalAdminFacade } from '../facades';
import { UserActions } from '../store/actions';
import { UserSelectors } from '../store/selectors';
import { getSearchPaginationAdminSelector } from '../store/selectors/order-approval.selector';
import { ORDER_APPROVALS_ADMIN, StateWithUser } from '../store/user-state';

@Injectable()
export class OrderApprovalAdminService implements OrderApprovalAdminFacade {
  constructor(
    private store: Store<StateWithUser & StateWithProcess<any>>,
    private userIdService: UserIdService,
    private authService: AuthService
  ) {}

  updateSearchParameters(searchParams: OrderApprovalSearchParams) {
    this.store.dispatch(new SearchAction(searchParams, ORDER_APPROVALS_ADMIN));
    this.loadOrderApprovals(searchParams);
  }

  getSearchParameters(): Observable<OrderApprovalSearchParams> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getSearchParamsAdminSelector).pipe(
      map((params) => params as OrderApprovalSearchParams),
      filter((params) => params && !!params.key)
    );
  }

  getPagination(): Observable<Pagination> {
    return this.store.select(getSearchPaginationAdminSelector);
  }

  private loadOrderApprovals(params: OrderApprovalSearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadOrderApprovalsAdmin(userId, params)),
      () => {}
    );
  }

  getOrderApprovals(_params?: OrderApprovalSearchParams): Observable<Array<Observable<OrderApproval>>> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());
    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(UserSelectors.OrderApprovalSelectors.getSearchPageResultsAdminSelector)),
      map((loaderState) => loaderState?.value?.results || []),
      map((results) =>
        results.map((key) => this.store.select(UserSelectors.OrderApprovalSelectors.getSearchResultAdminEntry, { key }))
      ),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getOrderApprovalsLoading(): Observable<boolean> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getSearchPageResultsAdminLoading);
  }

  getSorts(): Observable<Sort[]> {
    return this.store.select(UserSelectors.OrderApprovalSelectors.getSortsAdminSelector);
  }
}
